<template>
  <div class="col-12 comp-admin-friend-level my-3">
    <div class="row group-header">
      <div class="col-12 col-md-8">
        <router-link :to="'/friend/search/' + id + '/customer'" class="groupname">
          {{ friendshipGroup.name }}
        </router-link>
      </div>
    </div>
    <div v-if="primary === 'overview'" class="row">
      <div class="col-12">
        <friendshipcontact
          :contact-info-list="friendshipGroup.contactInfo"
          :editable="canEditContactInfo"
          @ContactAdd="onContactAdd"
          @ContactSave="onContactSave"
          @ContactDelete="onContactDelete"
        />
      </div>
      <div class="col-12">
        <SPRSimpleCheckbox
          v-model="friendshipGroup.friendRequestFormAllowed"
          :title="$t('userAdmin.friendRequestFormAllowed').toString()"
          @input="onFormAllowedChecked"
        />
      </div>

      
    </div>
    <div v-if="primary === 'overview'" class="col-12">
      <friendshipactivities
        :group-activities="friendshipGroup.groupActivities"
        :friendship-management-group-id="friendshipGroup.id"
        @ActivityEdit="onActivityEdit"
        @ActivitySave="onActivitySave"
        @ActivityCancel="onActivityCancel"
        can-edit
      />
    </div>
    <!--still loading-->
    <div v-if="!loaded" class="row mb-2">
      <div class="col-12 brand-red text-center">
        <loading />
      </div>
    </div>

    <!-- error has occured, reload option -->
    <div v-if="loaded === -1" class="row mb-2">
      <div class="col-12 brand-red text-center">
        <span class="cap-label d-block mb-2">{{ $t('userAdmin.friendshipLoadFailed') }}</span>
        <a class="d-block retry-area"
           href="javascript:void(0)"
           tabindex="0"
           @click="triggerFetch"
        >
          <font-awesome-icon icon="redo" size="2x" fixed-width aria-hidden="true" /><br>
          <span class="cap-label brand-red">{{ $t('userAdmin.retryLoad') }}</span>
        </a>
      </div>
    </div>

    <adminnavigation v-if="primary === 'overview'" :options="optionsConstant" />

    <div v-if="primary === 'overview'" class="col-12">
      <dl>
        <dt v-if="memberSections">
          {{ $t('userAdmin.friendshipGroupMemberSections') }}
          <span
            v-if="!friendshipGroup.national"
            @click="sectionsAll = !sectionsAll"
          >
            <font-awesome-icon
              v-if="sectionsAll"
              icon="chevron-down"
              class="brand-red"
            />
            <font-awesome-icon v-else icon="chevron-up" class="brand-red" />
          </span>
        </dt>
        <dd v-if="memberSections && sectionsAll">
          {{ memberSections }}
        </dd>
        <br v-if="!sectionsAll" />
        <dt>
          {{ $t('userAdmin.friendshipGroupMemberZipCodes') }}
          <span v-if="!friendshipGroup.national" @click="zipsAll = !zipsAll">
            <font-awesome-icon
              v-if="zipsAll"
              icon="chevron-down"
              class="brand-red"
            />
            <font-awesome-icon v-else icon="chevron-up" class="brand-red" />
          </span>
        </dt>
        <dd
          v-if="!friendshipGroup.national && zipsAll"
          :class="{ 'few-zips': !zipsAll }"
        >
          {{ memberZips }}
        </dd>
        <dd v-if="friendshipGroup.national">
          {{ $t('userAdmin.national') }}
        </dd>
      </dl>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import adminnavigation from './FriendManagementGroupAdminNavigation.vue'
import sprbutton from '../SPRButton.vue'
import loading from '../SPRLoading.vue'
import BackButton from '../BackButton.vue'
import friendshipcontact from './FriendshipContact.vue'
import friendshipactivities from './FriendshipActivities.vue'
import cache from '../../mixins/cache.js'
import SPRSimpleCheckbox from '../SPRSimpleCheckbox.vue'
import { FriendManagementDto } from '@/types'
import ExcelJS from 'exceljs'
import stringhelper from '@/helpers/stringhelper'
import {
  OrderByFriendType,
  PreferredAges,
  TimeWithoutFriends,
  CustomerTypes,
  FriendshipTypes,
  NetCustomerTypes,
  NetFriendshipTypes,
  FindNetFriendManagementGroupContentCriteria,
  FindStandardFriendManagementGroupContentCriteria,
} from '@/types'

// the data object
export interface TemplateComponentData {
  optionsConstant: Array<object>
  friendshipGroup: FriendManagementDto
  tempGroupActivities: string
  loaded: number
  zipsAll: boolean
  sectionsAll: boolean
}

export default Vue.extend({
  name: 'FriendshipAdmin',
  components: {
    adminnavigation,
    sprbutton,
    loading,
    BackButton,
    friendshipcontact,
    SPRSimpleCheckbox,
    friendshipactivities,
  },
  mixins: [cache],
  props: {
    primary: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    },
    filter: {
      type: String,
      default: '',
    },
  },
  data(): TemplateComponentData {
    return {
      ///friend/search/' + this.id + '/customer
      optionsConstant: [
        {
          uri: '/friend/search/' + this.id + '/customer',
          text: this.$t('userAdmin.enterFriendshipGroup'),
          primary: 'enter',
        },
        {
          uri: '/admin/friendship/' + this.id + '/brokers',
          text: this.$t('userAdmin.friendshipBrokers'),
          primary: 'broker',
        },
        {
          uri: '/admin/friendship/' + this.id + '/statistics',
          text: this.$t('userAdmin.friendshipStatistics'),
          primary: 'statistics',
        },
        {
          uri: '/admin/friendship/' + this.id + '/clients',
          text: this.$t('userAdmin.friendshipClients'),
          primary: 'client',
        },
        {
          uri: '/admin/friendship/' + this.id + '/volunteers',
          text: this.$t('userAdmin.friendshipVolunteers'),
          primary: 'volunteer',
        },
        {
          uri: '/admin/friendship/' + this.id + '/pools',
          text: this.$t('userAdmin.friendshipPools'),
          primary: 'pools',
        },
      ],
      friendshipGroup: {
        id: 0,
        name: this.$t('userAdmin.toFriendshipLink').toString(),
        contactInfo: [],
        groupActivities: [],
        national: false,
        volunteerCount: 0,
        customerCount: 0,
        friendRequestFormAllowed: false,
        sections: [],
        zipCodes: [],
        sectionDtos: [],
        netFriendship: false,
        riskyFriendshipsCount: 0,
        riskyFriendships: [],
      },
      tempGroupActivities: '',
      loaded: 0,
      zipsAll: false,
      sectionsAll: false,
    }
  },
  computed: {
    memberSections(): string {
      return this.friendshipGroup.sections &&
        this.friendshipGroup.sections.length
        ? this.friendshipGroup.sections.join(', ')
        : ''
    },
    memberZips(): string {
      return this.friendshipGroup.zipCodes &&
        this.friendshipGroup.zipCodes.length
        ? this.friendshipGroup.zipCodes.join(', ')
        : ''
    },
    canEditContactInfo(): boolean {
      //@ts-ignore
      const hr = (this as any).hasRole
        ? (this as any).hasRole.bind(this)
        : () => false
      return (
        hr('Admin') ||
        hr('FriendshipAdmin') ||
        hr('DistrictFriendshipAdmin') ||
        hr('FriendshipGroupManager')
      )
    },
  },
  mounted() {
    this.triggerFetch()
    // Add export volunteers button if user has proper role
    const hr = (this as any).hasRole
      ? (this as any).hasRole.bind(this)
      : () => false
    if (hr('Admin') || hr('FriendshipGroupManager')) {
      const exportOption = {
        text: this.$t('userAdmin.exportVolunteerButton').toString(),
        primary: 'export',
        type: 'secondary',
        size: 4,
        // custom action instead of navigation
        action: () => this.exportVolunteers(),
      }
      // Avoid duplicates
      //@ts-ignore
      if (!this.optionsConstant.find((o) => o.primary === 'export')) {
        //@ts-ignore
        this.optionsConstant.push(exportOption)
      }
    }
  },
  methods: {
    enterFriendshipGroup: function () {
      var routeName = '/friend/search/' + this.id + '/customer'
      this.$router.push(routeName)
    },
    triggerFetch: function () {
      this.loaded = 0
      var url =
        this.$store.state.apiBases.friendservice +
        this.$store.state.apiEndpoints.friendOrganizationById[0].replace(
          '{0}',
          this.id
        )
      var self = this
      this.fetchItem(
        url,
        this.expSeconds(120),
        (s) => {
          self.friendshipGroup = s
          self.loaded = 1
        }, () => { self.loaded = -1 })
      },
      updateFmg (): any {
        var url = this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.updateFmg[0]
       return this.$http.put(url, this.friendshipGroup)
      },
      onContactAdd (contact) {
        this.friendshipGroup.contactInfo.push(contact)
        this.updateFmg().then((response: any) => {
          if (response.ok) {
            // reload with the new ids
            this.friendshipGroup = response.body
          }
        })
      },
      onFormAllowedChecked () {
        this.updateFmg()
      },
      onActivitySave () {
        this.updateFmg().then(response => {
          if (response.ok) {
            // reload with the new ids
            this.friendshipGroup = response.body
          }
          else {
            console.error('response error', response)
          }
        }, error => {
          console.error('error', error)
        })
      },
      onActivityEdit () {
        // In order to rollback in case of cancel, I will have to store the current state of the groupActivities
        this.tempGroupActivities = JSON.stringify(this.friendshipGroup.groupActivities)
      },
      onActivityCancel () {
        // In case I cancel I want to restore the groupActivities to the previous state
        if (this.tempGroupActivities)
          this.friendshipGroup.groupActivities = JSON.parse(this.tempGroupActivities)
      },
      onContactSave (contact) {
        this.friendshipGroup.contactInfo = this.friendshipGroup.contactInfo.map(x => x.id === contact.id ? contact : x)
        this.updateFmg().then(response => {
          if (response.ok) {
            // reload with the new ids
            this.friendshipGroup = response.body
          }
        })
      },
      onContactDelete (id) {
        //@ts-ignore
        _.remove(this.friendshipGroup.contactInfo, (add) => {
          return add.id === id
        })

      this.updateFmg().then((response) => {
        if (response.ok) {
          // reload with the new ids
          this.friendshipGroup = response.body
        }
      })
    },
    exportVolunteers() {
      // Export volunteers replicating criteria shape used in UserSearch.vue but for all (skip/take 0)
      if (!this.id) return
      const groupIdNum = Number(this.id)
      const isNet = this.friendshipGroup.netFriendship
      // Map customerStates objects to enum ids if necessary
      const customerStateIds = Array.isArray(this.$store.state.customerStates)
        ? this.$store.state.customerStates.map((cs: any) =>
            typeof cs === 'number'
              ? cs
              : typeof cs.id === 'number'
              ? cs.id
              : cs.id?.value
          )
        : []
      const base: any = {
        skip: 0,
        take: 0,
        searchString: '',
        orderByFriendType: OrderByFriendType.OldestFirst,
        ages: PreferredAges.NotDefined,
        timeWithoutFriends: TimeWithoutFriends.NotDefined,
        customerStates: customerStateIds,
        languages: [],
        natives: [],
        groupMembership: false,
        genders: [],
        includeFriendPools: false,
        includeFriendships: false,
        groupId: groupIdNum,
      }
      const params:
        | FindNetFriendManagementGroupContentCriteria
        | FindStandardFriendManagementGroupContentCriteria = isNet
        ? {
            ...base,
            customerTypes: NetCustomerTypes.NotDefined,
            friendshipTypes: NetFriendshipTypes.NotDefined,
          }
        : {
            ...base,
            customerTypes: CustomerTypes.NotDefined,
            friendshipTypes: FriendshipTypes.NotDefined,
          }

      const url = isNet
        ? this.$store.state.apiBases.friendservice +
          this.$store.state.apiEndpoints.volunteerexportnet[0]
        : this.$store.state.apiBases.friendservice +
          this.$store.state.apiEndpoints.volunteerexportstandard[0]

      // Ensure pure JSON serializable (no circular references, no $id fields)
      const cleanPayload = JSON.parse(JSON.stringify(params))
      this.$http
        .post(url, cleanPayload, {
          headers: { 'Content-Type': 'application/json' },
        })
        .then((response: any) => {
          // Expecting array of volunteers similar to previous method
          const workbook = new ExcelJS.Workbook()
          const worksheet = workbook.addWorksheet('Volunteers')
          worksheet.columns = [
            { header: 'Etunimi', key: 'firstName', width: 30 },
            { header: 'Sukunimi', key: 'lastName', width: 30 },
            { header: 'Sähköposti', key: 'email', width: 30 },
            { header: 'Puhelin', key: 'phone', width: 30 },
            { header: 'Tila', key: 'state', width: 30 },
          ]
          const headerRow = worksheet.getRow(1)
          headerRow.font = { bold: true }
          response.body.forEach((member: any) => {
            worksheet.addRow({
              firstName: member.firstName,
              lastName: member.lastName,
              email: member.email,
              phone: member.phoneNumber,
              state: this.$t(
                'customerStates.' + stringhelper.toCamelCase(member.state),
                'fi'
              ),
            })
          })
          headerRow.eachCell((cell: any) => {
            cell.alignment = { vertical: 'middle', horizontal: 'center' }
          })
          worksheet.autoFilter = 'A1:E1'
          return workbook.xlsx.writeBuffer()
        })
        .then(
          (buffer: any) => {
            if (!buffer) return
            const link = document.createElement('a')
            link.href = URL.createObjectURL(
              new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              })
            )
            const dateAsTimestamp = new Date()
              .toISOString()
              .substr(0, 19)
              .replace(/:/g, '-')
              .replace('T', '-')
            link.download = `VolunteerExport-${this.id}-${dateAsTimestamp}.xlsx`
            document.body.appendChild(link)
            link.click()
            link.remove()
          },
          (err: Error) => {
            console.error('Export failed', err)
            this.$store.dispatch('alert/error', {
              message: this.$t('report.failed'),
            })
          }
        )
    },
  },
})
</script>

<style lang="scss">
  @import '~@/assets/scss/_variables.scss';
  .comp-admin-friend-level {
    .groupname {
      display: inline-block;
      margin-top: 5px;
      vertical-align: top;
      margin-right: 10px;
    }
    .enter-friendship-group {
      font-size: 13px;
      line-height: 20px;
      margin-right: -15px;
    }
    .few-zips {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .group-header {
      font-family: Signa-Bold,Sans-Serif;
      font-size: 32px;
      line-height: 42px;
      padding-bottom: 20px;
    }
  }
</style>
